import { useState } from "react";
import axios from "axios";
import { Send, Loader, Video, Mic as MicIcon, PenLine } from "lucide-react";
import { VintageMegaphone } from "@/components/PeopleIcons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { v: "video", label: "Vídeo", icon: Video },
  { v: "audio", label: "Voz", icon: MicIcon },
  { v: "escrito", label: "Escrito", icon: PenLine },
];

export default function VocesPage() {
  const [format, setFormat] = useState("");
  const [story, setStory] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!format) {
      toast.error("Elige un formato (vídeo, audio o por escrito)");
      return;
    }
    if (story.trim().length < 10) {
      toast.error("Cuéntanos un poco más de tu historia (mín. 10 caracteres)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/voice-requests`,
        { format, story_summary: story.trim(), anonymous },
        { withCredentials: true }
      );
      toast.success("Tu solicitud ha llegado. Nos pondremos en contacto contigo.");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="voces-page" className="max-w-lg mx-auto px-4 py-6 pb-10">
      {/* Hero */}
      <div
        className="rounded-3xl p-6 mb-5 border border-[#C44A3A]/15"
        style={{ background: "linear-gradient(135deg, #C44A3A22, #FDFAF4 60%, #C44A3A0F)" }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "#C44A3A22" }}
          >
            <VintageMegaphone size={24} strokeWidth={1.6} className="text-[#C44A3A]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#C44A3A] font-semibold mb-1">
              Voces Refugium
            </p>
            <h1 className="font-heading text-2xl text-[#5B3E2E] leading-tight">
              Cuenta tu historia
            </h1>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="mb-6 px-1">
        <p className="text-sm text-[#5B3E2E]/85 leading-relaxed mb-3">
          <span className="text-[#C44A3A] font-medium">Voces Refugium</span> es un
          espacio donde los miembros de la comunidad comparten su propio duelo —
          en el formato que mejor les encaje (<strong>vídeo, grabación de voz o texto</strong>) —
          para que la historia de unos sirva de inspiración y refugio para otros.
          Cada historia se publica en el panel de publicaciones de la app, disponible solo para los miembros.
        </p>
        <p className="text-sm text-[#5B3E2E]/70 leading-relaxed">
          Si te ves preparad@ y crees que tu historia puede inspirar a otras personas,
          rellena la solicitud y nos pondremos en contacto contigo por email para
          acordar la fecha y todo lo necesario. Todo lo que compartas es{" "}
          <strong>voluntario</strong> y tú decides hasta dónde contar.
        </p>
      </div>

      {sent ? (
        <div
          data-testid="voces-success"
          className="rounded-3xl p-8 text-center border border-[#6B9070]/20"
          style={{ background: "linear-gradient(135deg, #6B907018, #FDFAF4 60%)" }}
        >
          <div className="w-14 h-14 rounded-full bg-[#6B9070]/15 flex items-center justify-center mx-auto mb-3">
            <Send size={22} className="text-[#6B9070]" />
          </div>
          <h3 className="font-heading text-xl text-[#5B3E2E] mb-2">
            Hemos recibido tu historia
          </h3>
          <p className="text-sm text-[#5B3E2E]/70 leading-relaxed">
            Gracias por confiar en Refugium. Te escribiremos pronto por email para
            acordar los detalles.
          </p>
        </div>
      ) : (
        <>
          {/* Format picker */}
          <div className="mb-5">
            <p className="text-xs text-[#5B3E2E]/75 mb-2 font-semibold px-1">
              ¿En qué formato te sentirías más cómod@?
            </p>
            <div className="grid grid-cols-3 gap-2">
              {FORMATS.map((opt) => {
                const Icon = opt.icon;
                const active = format === opt.v;
                return (
                  <button
                    key={opt.v}
                    data-testid={`voces-format-${opt.v}`}
                    onClick={() => setFormat(opt.v)}
                    className={`py-4 rounded-2xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1.5 ${
                      active
                        ? "border-[#C44A3A] bg-[#C44A3A]/10 text-[#C44A3A]"
                        : "border-[#5B3E2E]/12 text-[#5B3E2E]/60 hover:border-[#5B3E2E]/25 bg-white"
                    }`}
                  >
                    <Icon size={20} strokeWidth={1.6} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Story */}
          <div className="mb-5">
            <p className="text-xs text-[#5B3E2E]/75 mb-1.5 font-semibold px-1">
              Cuéntanos brevemente{" "}
              <span className="text-[#5B3E2E]/45 font-normal">
                (qué duelo estás atravesando, qué te gustaría compartir…)
              </span>
            </p>
            <Textarea
              data-testid="voces-story"
              value={story}
              onChange={(e) => setStory(e.target.value)}
              placeholder="Con las palabras que sientas. No hace falta que sea largo."
              className="bg-white border-[#5B3E2E]/15 rounded-2xl text-[#5B3E2E] min-h-[140px]"
              style={{ fontSize: "16px" }}
              maxLength={2000}
            />
            <p className="text-[10px] text-[#5B3E2E]/35 mt-1 text-right px-1">
              {story.length}/2000
            </p>
          </div>

          {/* Anonymous checkbox */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none mb-6 px-1">
            <input
              data-testid="voces-anonymous"
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#C44A3A] flex-shrink-0"
            />
            <span className="text-[13px] text-[#5B3E2E]/75 leading-relaxed">
              Quiero mantenerme <strong>anónimo/a</strong> cuando se publique la
              historia (usaremos un alias o solo tu voz).
            </span>
          </label>

          <Button
            data-testid="voces-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full text-white rounded-full py-6 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_6px_20px_rgba(196,74,58,0.3)]"
            style={{ background: "linear-gradient(135deg, #C44A3A, #A83A2D)" }}
          >
            {submitting ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            {submitting ? "Enviando…" : "Enviar solicitud"}
          </Button>

          <p className="text-[11px] text-[#5B3E2E]/50 text-center mt-4 leading-relaxed">
            También puedes escribirnos directamente a{" "}
            <a
              href="mailto:espacio_refugium@outlook.com?subject=Voces%20Refugium%20-%20quiero%20contar%20mi%20historia"
              className="text-[#C44A3A] underline"
            >
              espacio_refugium@outlook.com
            </a>
          </p>
        </>
      )}
    </div>
  );
}
